import { DIMENSIONS, UNIT_CONVERSIONS, UNITS } from '../../constants';
import constants from '../../settings/constants';
import {
  getProductMeasurementRemapKeys,
  getAllDimensionsKeys,
  getGlobalType,
} from '../../state/products/productsHelpers.ts';
import { findArticle } from './articles';
import productService from '.';

export const gatherArticleMeasurements = productIds => {
  const gatherFromId = productService.isOnlyAvailableInMultipack(productIds[0])
    ? constants.BULK_ARTICLES[productIds[0]].id
    : productIds[0];
  const article = findArticle(gatherFromId);
  const numbersRegex = /\d+/g;
  const lettersRegex = /[a-zA-Z]+/g;

  /**
   * Derives unit from string. If no unit is found, defaults to CM
   *
   * @param string
   * @returns {*|string}
   */
  const deriveUnit = string => {
    const unit = string.match(lettersRegex);
    return unit ? unit[0] : UNITS.cm;
  };

  /**
   * Regexes out numbers and letters from measurements string
   *
   * @param string {string}
   * @returns {number}
   */
  const deriveMeasurementsFromKompisData = string =>
    parseInt(string.match(numbersRegex)[0], 10) *
    UNIT_CONVERSIONS[deriveUnit(string)];

  /**
   * Returns an object with articles dimensions as key
   * @param art
   * @returns {T | *}
   */
  const getDimensionsAsObject = art => {
    return art.measure.reduce((acc, { typeCode, ...rest }) => {
      const globalType = getGlobalType(typeCode);
      return {
        ...acc,
        ...(globalType && {
          [globalType.toLowerCase()]: rest,
        }),
      };
    }, {});
  };

  /**
   * Returns an object with articles measurements
   * @param art
   * @returns {{}|boolean}
   */
  const deriveArticleMeasurements = art => {
    if (!art?.hasOwnProperty('measure')) return false;

    const dimensionsAsObject = getDimensionsAsObject(art);

    return [...getAllDimensionsKeys()].reduce((acc, dimension) => {
      if (!dimensionsAsObject.hasOwnProperty(dimension)) return acc;

      const value = deriveMeasurementsFromKompisData(
        dimensionsAsObject[dimension].textMetric
      );

      return value ? { ...acc, [dimension]: value } : acc;
    }, {});
  };

  /**
   * Returns all products as an object with productID as key
   *
   * @returns {object}
   */
  const getArticlesDimensionsByIds = () =>
    productIds.reduce((acc, id) => {
      const data = deriveArticleMeasurements(findArticle(id));
      return data ? [...acc, data] : acc;
    }, []);

  /**
   * Remodels the data in the case where IKEA's data's keys don't match out keys.
   * Eventual mapping is found in the settings-file for said range
   *
   * @param articles
   * @returns {*}
   */
  const handleDataRemodel = articles => {
    const data = getProductMeasurementRemapKeys(productIds);

    return data || !articles
      ? articles.map(article =>
          Object.keys(data).reduce(
            (acc, dimension) => ({
              ...acc,
              [data[dimension]]: article[dimension],
            }),
            {}
          )
        )
      : articles;
  };

  /**
   * returns keys for deriving min and max values for dimension from data
   *
   * @param dimension {string}
   * @returns {string[]}
   */
  const getMinMaxKeys = dimension => [`Min. ${dimension}`, `Max. ${dimension}`];

  /**
   * Find specific measurement from article data
   *
   * @param measurements {array}
   * @param measurement {string}
   * @returns {string}
   */
  const findMeasurements = (measurements, measurement) => {
    return measurements.find(
      ({ typeCode }) => getGlobalType(typeCode) === measurement
    )?.textMetric;
  };

  /**
   * Finds min-max values for measurement
   *
   * @param measurements {array}
   * @returns {function(*, *=): *|*[]}
   */
  const deriveMinMaxMeasurement = measurements => (acc, measurement) => {
    const foundMeasurements = findMeasurements(measurements, measurement);

    return !foundMeasurements
      ? acc
      : [...acc, deriveMeasurementsFromKompisData(foundMeasurements)];
  };

  /**
   * Gathers min-max values for dimension on an article
   *
   * @returns {*}
   */
  const getMinMaxValues = () =>
    !article.measure
      ? {}
      : Object.keys(DIMENSIONS).reduce((acc, dimension) => {
          const keys = getMinMaxKeys(dimension);
          const foundMinMaxMeasurements = keys.reduce(
            deriveMinMaxMeasurement(article.measure),
            []
          );

          return foundMinMaxMeasurements.length
            ? { ...acc, [dimension]: foundMinMaxMeasurements }
            : acc;
        }, {});

  const minMaxValues = getMinMaxValues();

  return {
    article,
    minMaxValues,
    articleArray: handleDataRemodel(getArticlesDimensionsByIds()),
  };
};
